import React, { useEffect, useState } from 'react';
import { Search, X, RefreshCw, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { HistoryPeriodSummary } from './HistoryPeriodSummary';
import { HistoryRecordRow } from './HistoryRecordRow';
import './historyList.css';

const RANGES = [{ value: '30', label: 'Last 30 days' }, { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last year' }, { value: 'all', label: 'All time' }];

export const HistoryList = ({ events, totalLoaded, search, setSearch, range, setRange, loading, error, onRetry, ...actions }) => {
  const [expanded, setExpanded] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  useEffect(() => { setVisibleCount(50); setExpanded(null); }, [search, range]);
  return <div className="history-report" data-testid="history-list-view">
    <div className="history-controls">
      <div className="history-search">
        <Search size={16} aria-hidden="true" />
        <Input data-testid="history-search-input" aria-label="Search maintenance history" placeholder="Search equipment, work, parts or technician…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button type="button" onClick={() => setSearch('')} data-testid="history-search-clear-btn" aria-label="Clear search" title="Clear search"><X size={15} /></button>}
      </div>
      <div className="history-ranges" role="group" aria-label="History period">
        {RANGES.map((r) => <button key={r.value} type="button" aria-pressed={range === r.value}
          data-testid={`history-range-chip-${r.value}`} onClick={() => setRange(r.value)}>{r.label}</button>)}
      </div>
    </div>
    {search.trim() && range !== 'all' && <div className="history-search-scope" data-testid="history-search-widen-hint">
      <span>Search period: {RANGES.find((r) => r.value === range)?.label.toLowerCase()}</span>
      <button type="button" onClick={() => setRange('all')} data-testid="history-search-widen-btn">Search all history →</button>
    </div>}
    <HistoryPeriodSummary events={events} loading={loading} error={error} rangeLabel={RANGES.find((r) => r.value === range)?.label} searching={!!search.trim()} />
    {error ? <div role="alert" className="history-list-state" data-testid="history-list-error">
      <p>{error}</p><Button variant="outline" onClick={onRetry} data-testid="history-retry-btn"><RefreshCw size={14} /> Retry</Button>
    </div> : loading ? <div role="status" className="history-list-state" data-testid="history-list-loading">Loading maintenance history…</div>
      : events.length === 0 ? <div className="history-list-state" data-testid="history-list-empty">
        <p>{search.trim() ? 'No records match your search in this period.' : 'No maintenance records in this period.'}</p>
        {search.trim() ? <Button variant="outline" onClick={() => setSearch('')} data-testid="history-empty-clear-btn">Clear search</Button>
          : range !== 'all' && <Button variant="outline" onClick={() => setRange('all')} data-testid="history-empty-all-btn">View all history</Button>}
      </div> : <>
        <div className="history-list-head history-row-grid" aria-hidden="true">
          <span>Date</span><span>Equipment</span><span>Work performed / outcome</span><span className="history-labor-heading">Labor (h)</span><span>Follow-up</span><span />
        </div>
        <ol className="history-records" data-testid="history-records">
          {events.slice(0, visibleCount).map((event) => <HistoryRecordRow key={event.id} event={event} expanded={expanded === event.id}
            onToggle={() => setExpanded((id) => id === event.id ? null : event.id)} {...actions} />)}
        </ol>
        <div className="history-list-footer">
          <span data-testid="history-visible-count">Showing {Math.min(visibleCount, events.length)} of {events.length} records{search.trim() ? ` · ${totalLoaded} in period` : ''}</span>
          {visibleCount < events.length && <Button variant="outline" size="sm" data-testid="history-load-more-btn" onClick={() => setVisibleCount((count) => count + 50)}><ChevronDown size={14} /> Show more records</Button>}
        </div>
      </>}
  </div>;
};